import { Button, Stack, Tooltip, Typography } from '@mui/material'
import { ReactNode, useEffect, useMemo, useRef } from 'react'
import { IFlow, INode } from '../../../../../definitions/Nodes'
import { IAcceptedData } from '../../../../../definitions/NodesDescription'
import MappingResult from './MappingResult'
import { InfoRounded } from '@mui/icons-material'
import NodeManager from '../../../../../managers/NodeManager'

interface P {
    acceptedData: IAcceptedData<'stmt_settlement'>
    selectedNode: INode<'stmt_settlement'>
    flow: IFlow
    setSelected: React.Dispatch<React.SetStateAction<INode<'stmt_settlement'>>>
    disable: boolean
}

function NodeSettlementEditor({ selectedNode, flow, setSelected, disable }: P) {
    const scrollRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (scrollRef.current)
            scrollRef.current.scrollIntoView()
    }, [selectedNode])

    const inputNodes = useMemo(() => flow.nodes.filter(node => selectedNode.inputs.map(input => input.uuid).includes(node.uuid)), [])
    const shapes = useMemo(() => NodeManager.getInputShapes(selectedNode, flow.nodes), [])
    const memoizeItemsMap = useMemo(() => selectedNode.data.itemsMap, [selectedNode.data.itemsMap])

    const handleAdd = () => {
        setSelected(prev => ({
            ...prev,
            data: {
                ...prev.data,
                itemsMap: prev?.data?.itemsMap
                    ? [...prev.data.itemsMap, { fromInputNode: '', fromInputNodeOutputKey: '', settlementOutputKey: '' }]
                    : [{ fromInputNode: '', fromInputNodeOutputKey: '', settlementOutputKey: '' }]
            }
        }))
    }

    const renderInfoField = (title: string | ReactNode) => (
        <Tooltip arrow title={title} disableHoverListener={disable}>
            <InfoRounded fontSize='inherit' sx={{ cursor: disable ? undefined : 'pointer', color: disable ? 'var(--icon-disabled)' : 'var(--icon-color)' }} />
        </Tooltip>
    )

    return (
        <Stack spacing={2}>
            <Typography variant='h6'>Settlement mapping results</Typography>
            <Stack spacing={3}>
                <Stack direction='row' gap={2}>
                    <Stack direction='row' gap={1} flex={1} alignItems='center' width='25%'>
                        {renderInfoField('Insert the header of mapping result')}
                        <Typography variant='subtitle2'>Field name</Typography>
                    </Stack>
                    <Stack direction='row' gap={1} flex={1} alignItems='center' width='25%'>
                        {renderInfoField('Insert the node name as input source')}
                        <Typography variant='subtitle2'>Node Name</Typography>
                    </Stack>
                    <Stack direction='row' gap={1} flex={1} alignItems='center' width='25%'>
                        {renderInfoField('Insert the properties inside the node where to take the value')}
                        <Typography variant='subtitle2' flex={1}>Output key</Typography>
                    </Stack>
                    <Stack direction='row' gap={1} flex={1} alignItems='center' width='25%'>
                        {renderInfoField('Choose the format: percent, financial or clear')}
                        <Typography variant='subtitle2' flex={1}>Format</Typography>
                    </Stack>
                </Stack>
                {memoizeItemsMap?.map((item, idx) => {
                    return (
                        <Stack key={idx} spacing={2}>
                            <MappingResult
                                setSelected={setSelected}
                                shapes={shapes}
                                disable={disable}
                                item={item}
                                idx={idx}
                                inputNodes={inputNodes}
                            />
                        </Stack>
                    )
                })}
            </Stack>
            <Button
                ref={scrollRef}
                size='small'
                onClick={() => handleAdd()}
                disabled={disable || selectedNode?.data?.itemsMap?.some(item => !item.fromInputNode || !item.fromInputNodeOutputKey || !item.settlementOutputKey)}>
                Add new mapping result
            </Button>
        </Stack>
    )
}

export default NodeSettlementEditor
